import { tv } from 'tailwind-variants';
import { defineComponent, h } from 'vue';
import iconSets from './icon-sets.json';

/** @template T; @typedef {import("vue").PropType<T>} PropType<T> */
/** @typedef {import('./icon-types').IconName} IconName */

/**
 * @typedef {object} IconProps
 * @property {IconName} name - Icon name
 * @property {string} [className=''] - Additional class name for the icon
 * @property {('icon'|'secondary'|'disabled'|'brand'|'info'|'critical'|'success'|'caution'|'warning'|'magic'|'inverse')} [variantColor='default'] - Icon color variant
 */

// Create a icon caches
const iconCaches = new Map();

// Store the icon sets to the icon caches
Object.entries(iconSets.icons).forEach(([name, icon]) => {
  iconCaches.set(name, icon.body);
});

const iconRecipe = tv({
  base: '',
  variants: {
    variantColor: {
      icon: 'text-brand-gray-14 dark:text-brand-gray-12',
      secondary: 'text-brand-gray-12 dark:text-brand-gray-11',
      disabled: 'text-brand-gray-10',
      brand: 'text-brand-blue-11',
      info: 'text-brand-azure-11',
      critical: 'text-brand-red-12',
      success: 'text-brand-green-14',
      caution: 'text-brand-yellow-12',
      warning: 'text-brand-orange-12',
      magic: 'text-brand-purple-12',
      inverse: 'text-brand-gray-8',
    },
  },
  defaultVariants: {
    variantColor: '',
  },
});

export const CsIcon = defineComponent({
  name: 'CsIcon',
  props: {
    /** @type {PropType<IconProps['name']>} */
    name: {
      type: String,
      required: true,
    },
    /** @type {PropType<IconProps['className']>} */
    className: {
      type: String,
      default: '',
    },
    /** @type {PropType<IconProps['variantColor']>} */
    variantColor: {
      type: String,
      default: 'default',
    },
  },
  setup(props) {
    // Get the icon path from iconSets based on the name prop
    const getIconPath = name => {
      const icon = iconCaches.get(name);
      return icon || '';
    };

    return () =>
      h('svg', {
        attrs: {
          'data-component': 'CsIcon',
          'data-icon-name': props.name,
          'data-variant-color': props.variantColor,
          xmlns: 'http://www.w3.org/2000/svg',
          width: '20px',
          height: '20px',
          viewBox: '0 0 20 20',
        },
        domProps: {
          innerHTML: getIconPath(props.name),
        },
        class: iconRecipe(props, { className: props.className }),
      });
  },
});
